import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { useOrg } from "@/pages/DashboardLayout";
import { useTheme, useInstallPrompt } from "@/lib/theme";
import { getPushStatus, subscribeToPush, unsubscribeFromPush, sendTestPush, isPushSupported } from "@/lib/push";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Moon, Sun, Download, Check, Bell, BellOff, Zap } from "lucide-react";

export default function Settings() {
  const { org, reloadOrg } = useOrg();
  const { theme, setTheme } = useTheme();
  const { canInstall, installed, install } = useInstallPrompt();
  const [f, setF] = useState({});
  const [pushState, setPushState] = useState({ supported: false, permission: "default", subscribed: false });
  const [pushBusy, setPushBusy] = useState(false);

  const refreshPushState = async () => setPushState(await getPushStatus());

  useEffect(() => {
    (async () => {
      if (await isPushSupported()) await refreshPushState();
    })();
  }, []);

  const enablePush = async () => {
    setPushBusy(true);
    try {
      await subscribeToPush();
      toast.success("Notificaciones activadas");
      await refreshPushState();
    } catch (e) {
      toast.error(e.message || "No se pudo activar");
    } finally {
      setPushBusy(false);
    }
  };

  const disablePush = async () => {
    setPushBusy(true);
    try {
      await unsubscribeFromPush();
      toast.success("Notificaciones desactivadas");
      await refreshPushState();
    } catch (e) {
      toast.error("Error");
    } finally {
      setPushBusy(false);
    }
  };

  const testPush = async () => {
    try {
      const r = await sendTestPush();
      if (r?.sent > 0) toast.success("Notificación enviada a " + r.sent + " dispositivo(s)");
      else toast.error("No se pudo enviar. ¿Activaste las notificaciones?");
    } catch {
      toast.error("Error");
    }
  };

  useEffect(() => {
    if (org) {
      setF({
        name: org.name || "",
        logo_url: org.logo_url || "",
        brand_color: org.brand_color || "#10B981",
        phone: org.phone || "+549 ",
        address: org.address || "",
        description: org.description || "",
        open_hour: org.open_hour ?? 8,
        close_hour: org.close_hour ?? 23,
        cancel_cutoff_hours: org.cancel_cutoff_hours ?? 3,
        social_instagram: org.social_instagram || "",
        social_facebook: org.social_facebook || "",
        social_website: org.social_website || "",
      });
    }
  }, [org]);

  const save = async () => {
    try {
      await api.patch("/orgs/me", {
        ...f,
        open_hour: parseInt(f.open_hour),
        close_hour: parseInt(f.close_hour),
        cancel_cutoff_hours: parseInt(f.cancel_cutoff_hours),
      });
      toast.success("Guardado");
      reloadOrg();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    }
  };

  if (!org) return null;

  return (
    <div>
      <div className="mb-10">
        <div className="text-xs uppercase tracking-[0.3em] text-emerald-400 mb-2">// Personalizá tu club</div>
        <h1 className="font-heading text-5xl tracking-tight">AJUSTES</h1>
      </div>

      <div className="max-w-3xl space-y-10">
        <section className="bg-[#141414] border border-[#262626] rounded-lg p-8">
          <h2 className="font-heading text-2xl tracking-wider mb-6">IDENTIDAD</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="uppercase tracking-widest text-xs text-zinc-400">Nombre del club</Label>
              <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="bg-[#0A0A0A] border-[#262626] mt-1" data-testid="set-name" />
            </div>
            <div>
              <Label className="uppercase tracking-widest text-xs text-zinc-400">URL del logo</Label>
              <Input value={f.logo_url} onChange={(e) => setF({ ...f, logo_url: e.target.value })} className="bg-[#0A0A0A] border-[#262626] mt-1" placeholder="https://..." data-testid="set-logo" />
            </div>
            <div>
              <Label className="uppercase tracking-widest text-xs text-zinc-400">Color de marca</Label>
              <div className="flex gap-2 mt-1">
                <Input type="color" value={f.brand_color} onChange={(e) => setF({ ...f, brand_color: e.target.value })} className="bg-[#0A0A0A] border-[#262626] w-20 h-10 p-1" data-testid="set-color" />
                <Input value={f.brand_color} onChange={(e) => setF({ ...f, brand_color: e.target.value })} className="bg-[#0A0A0A] border-[#262626] flex-1" />
              </div>
            </div>
            <div>
              <Label className="uppercase tracking-widest text-xs text-zinc-400">WhatsApp del club</Label>
              <Input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} className="bg-[#0A0A0A] border-[#262626] mt-1" placeholder="+549 11 2345 6789" data-testid="set-phone" />
            </div>
            <div className="md:col-span-2">
              <Label className="uppercase tracking-widest text-xs text-zinc-400">Dirección</Label>
              <Input value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} className="bg-[#0A0A0A] border-[#262626] mt-1" data-testid="set-address" />
            </div>
            <div className="md:col-span-2">
              <Label className="uppercase tracking-widest text-xs text-zinc-400">Descripción</Label>
              <Textarea value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className="bg-[#0A0A0A] border-[#262626] mt-1" rows={3} data-testid="set-desc" />
            </div>
          </div>
        </section>

        <section className="bg-[#141414] border border-[#262626] rounded-lg p-8">
          <h2 className="font-heading text-2xl tracking-wider mb-6">HORARIOS</h2>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="uppercase tracking-widest text-xs text-zinc-400">Apertura (0-23)</Label>
              <Input type="number" min="0" max="23" value={f.open_hour} onChange={(e) => setF({ ...f, open_hour: e.target.value })} className="bg-[#0A0A0A] border-[#262626] mt-1" data-testid="set-open" />
            </div>
            <div>
              <Label className="uppercase tracking-widest text-xs text-zinc-400">Cierre (0-23)</Label>
              <Input type="number" min="0" max="23" value={f.close_hour} onChange={(e) => setF({ ...f, close_hour: e.target.value })} className="bg-[#0A0A0A] border-[#262626] mt-1" data-testid="set-close" />
            </div>
            <div className="col-span-2">
              <Label className="uppercase tracking-widest text-xs text-zinc-400">Cancelación gratuita hasta (horas antes)</Label>
              <Input type="number" min="0" max="72" value={f.cancel_cutoff_hours} onChange={(e) => setF({ ...f, cancel_cutoff_hours: e.target.value })} className="bg-[#0A0A0A] border-[#262626] mt-1" data-testid="set-cutoff" />
              <p className="text-xs text-zinc-500 mt-1">Cuántas horas antes del turno el cliente puede cancelar solo desde el link.</p>
            </div>
          </div>
        </section>

        <section className="bg-[#141414] border border-[#262626] rounded-lg p-8">
          <h2 className="font-heading text-2xl tracking-wider mb-6">REDES</h2>
          <div className="space-y-4">
            <div>
              <Label className="uppercase tracking-widest text-xs text-zinc-400">Instagram</Label>
              <Input value={f.social_instagram} onChange={(e) => setF({ ...f, social_instagram: e.target.value })} className="bg-[#0A0A0A] border-[#262626] mt-1" placeholder="https://instagram.com/..." data-testid="set-ig" />
            </div>
            <div>
              <Label className="uppercase tracking-widest text-xs text-zinc-400">Facebook</Label>
              <Input value={f.social_facebook} onChange={(e) => setF({ ...f, social_facebook: e.target.value })} className="bg-[#0A0A0A] border-[#262626] mt-1" data-testid="set-fb" />
            </div>
            <div>
              <Label className="uppercase tracking-widest text-xs text-zinc-400">Sitio web</Label>
              <Input value={f.social_website} onChange={(e) => setF({ ...f, social_website: e.target.value })} className="bg-[#0A0A0A] border-[#262626] mt-1" data-testid="set-web" />
            </div>
          </div>
        </section>

        <section className="bg-[#141414] border border-[#262626] rounded-lg p-8">
          <h2 className="font-heading text-2xl tracking-wider mb-6">APARIENCIA</h2>
          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              onClick={() => setTheme("dark")}
              className={`p-5 rounded-lg border-2 text-left transition ${theme === "dark" ? "border-emerald-500 bg-emerald-500/10" : "border-[#262626] hover:border-zinc-700"}`}
              data-testid="theme-dark-btn"
            >
              <Moon className="h-5 w-5 text-emerald-400 mb-3" strokeWidth={1.5} />
              <div className="font-heading text-lg tracking-wider">OSCURO</div>
              <div className="text-xs text-zinc-500 mt-1">Por defecto. Ideal de noche.</div>
              {theme === "dark" && <Check className="h-4 w-4 text-emerald-400 mt-3" />}
            </button>
            <button
              type="button"
              onClick={() => setTheme("light")}
              className={`p-5 rounded-lg border-2 text-left transition ${theme === "light" ? "border-emerald-500 bg-emerald-500/10" : "border-[#262626] hover:border-zinc-700"}`}
              data-testid="theme-light-btn"
            >
              <Sun className="h-5 w-5 text-emerald-400 mb-3" strokeWidth={1.5} />
              <div className="font-heading text-lg tracking-wider">CLARO</div>
              <div className="text-xs text-zinc-500 mt-1">Fondos claros. Ideal de día.</div>
              {theme === "light" && <Check className="h-4 w-4 text-emerald-400 mt-3" />}
            </button>
          </div>
        </section>

        <section className="bg-[#141414] border border-[#262626] rounded-lg p-8">
          <h2 className="font-heading text-2xl tracking-wider mb-2">NOTIFICACIONES PUSH</h2>
          <p className="text-zinc-400 text-sm mb-6">
            Recibí una alerta en este dispositivo cada vez que alguien reserva un turno — incluso con el navegador cerrado (si instalaste la app).
          </p>
          {!pushState.supported ? (
            <div className="p-4 bg-[#0A0A0A] border border-[#262626] rounded text-sm text-zinc-400" data-testid="push-unsupported">
              Este navegador no soporta notificaciones push. Probá desde Chrome, Edge o Firefox en desktop/Android.
            </div>
          ) : pushState.permission === "denied" ? (
            <div className="p-4 bg-red-500/10 border border-red-500/30 rounded text-sm text-red-400" data-testid="push-denied">
              Las notificaciones están bloqueadas. Habilitalas desde la configuración del navegador (icono 🔒 en la barra de direcciones).
            </div>
          ) : pushState.subscribed ? (
            <div className="space-y-4">
              <div className="flex items-center gap-3 p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-lg text-emerald-400" data-testid="push-enabled">
                <Bell className="h-5 w-5" />
                <div className="flex-1">
                  <div className="uppercase tracking-widest text-xs font-bold">Activadas</div>
                  <div className="text-xs text-zinc-400 mt-0.5">Recibirás un aviso por cada nueva reserva.</div>
                </div>
              </div>
              <div className="flex gap-3 flex-wrap">
                <Button type="button" onClick={testPush} className="bg-amber-500 hover:bg-amber-600 text-black uppercase tracking-widest text-xs font-bold" data-testid="push-test-btn">
                  <Zap className="h-3 w-3 mr-2" /> Enviar prueba
                </Button>
                <Button type="button" variant="outline" onClick={disablePush} disabled={pushBusy} className="border-zinc-700 bg-transparent text-zinc-400 hover:bg-[#141414] uppercase tracking-widest text-xs" data-testid="push-off-btn">
                  <BellOff className="h-3 w-3 mr-2" /> Desactivar
                </Button>
              </div>
            </div>
          ) : (
            <Button
              type="button"
              onClick={enablePush}
              disabled={pushBusy}
              className="bg-emerald-500 hover:bg-emerald-600 text-black uppercase tracking-widest font-bold h-12 px-6"
              data-testid="push-on-btn"
            >
              <Bell className="h-4 w-4 mr-2" /> {pushBusy ? "Activando..." : "Activar notificaciones"}
            </Button>
          )}
        </section>

        <section className="bg-[#141414] border border-[#262626] rounded-lg p-8">
          <h2 className="font-heading text-2xl tracking-wider mb-2">INSTALAR APP</h2>
          <p className="text-zinc-400 text-sm mb-6">
            Instalá Messirve en tu celular o computadora para acceder con un toque, sin navegador y con pantalla completa.
          </p>
          {installed ? (
            <div className="flex items-center gap-3 p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-lg text-emerald-400" data-testid="pwa-installed">
              <Check className="h-5 w-5" />
              <div>
                <div className="uppercase tracking-widest text-xs font-bold">Instalado</div>
                <div className="text-xs text-zinc-400 mt-0.5">Ya estás usando Messirve como aplicación.</div>
              </div>
            </div>
          ) : canInstall ? (
            <Button
              type="button"
              onClick={install}
              className="bg-emerald-500 hover:bg-emerald-600 text-black uppercase tracking-widest font-bold h-12 px-6"
              data-testid="pwa-install-btn"
            >
              <Download className="h-4 w-4 mr-2" /> Instalar ahora
            </Button>
          ) : (
            <div className="p-4 bg-[#0A0A0A] border border-[#262626] rounded-lg text-sm text-zinc-400 space-y-2" data-testid="pwa-manual">
              <div className="uppercase tracking-widest text-xs text-zinc-500 font-bold">Instrucciones manuales</div>
              <div>📱 <b className="text-white">iPhone (Safari):</b> Compartir → "Agregar a pantalla de inicio".</div>
              <div>📱 <b className="text-white">Android (Chrome):</b> Menú ⋮ → "Instalar aplicación".</div>
              <div>💻 <b className="text-white">Desktop (Chrome/Edge):</b> Barra de direcciones → icono de instalar.</div>
            </div>
          )}
        </section>

        <Button onClick={save} className="bg-emerald-500 hover:bg-emerald-600 text-black uppercase tracking-widest font-bold px-10 h-12" data-testid="save-btn">
          Guardar cambios
        </Button>
      </div>
    </div>
  );
}
